import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
  FormFeedback,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { FaPlus, FaPen, FaTrash, FaStar } from "react-icons/fa";

import api from "services/api";
import { useToast } from "hooks/toast";
import { useCompanies } from "hooks/companies";
import useDebounce from "hooks/useDebounce";
import SpecificEmpresaRequiredNotice from "components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import SortHeader from "components/SortHeader";
import TablePagination from "components/TablePagination";
import { toggleSort, sortRows, paginate } from "utils/tableHelpers";
import { reactSelectStyles, reactSelectPortalProps } from "utils/reactSelectStyles";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import ShellCard, { TableSurface } from "components/ShellCard";
import Status from "components/Status";

// Tela padronizada no épico PO1-2723 (história PO1-2731): hero, faixa de
// filtros, tabela ordenável/paginada e modais por token, sem cor cravada.

const URL_BASE = "/cadastros/negocios";
const STATUS_ALL = "__ALL__";

const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: "Todos" },
  { value: "active", label: "Ativos" },
  { value: "inactive", label: "Inativos" },
];

const DEFAULT_SORT = { field: "codigo", dir: "asc" };

function createEmptyForm() {
  return {
    codigo: "",
    nome: "",
    descricao: "",
    status: "active",
    padrao: false,
  };
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function isActive(status) {
  if (typeof status === "boolean") return status;
  const normalized = normalizeText(status);
  return !["0", "false", "inativo", "nao"].includes(normalized);
}

function isPadrao(padrao) {
  return padrao === 1 || padrao === true || padrao === "1";
}

// Valor usado na ordenação client-side por coluna.
function sortValue(row, field) {
  if (field === "padrao") return isPadrao(row.padrao) ? 1 : 0;
  if (field === "status") return isActive(row.status) ? 1 : 0;
  return row[field];
}

const stateBoxStyle = {
  textAlign: "center",
  padding: "2.5rem 1rem",
  color: AUTOPILOT_COLORS.muted,
};

const stateTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.98rem",
  fontWeight: 700,
  marginBottom: "0.35rem",
};

const mutedCellStyle = { color: AUTOPILOT_COLORS.muted };

const modalSubtitleStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.8rem",
  marginBottom: "1rem",
};

const modalFooterStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "0.5rem",
  marginTop: "1rem",
};

const Negocios = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(createEmptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { addToast } = useToast();
  const { empresa, empresaId, hasEmpresaSelection } = useCompanies();
  const debouncedSearch = useDebounce(searchTerm, 250);

  const loadData = useCallback(async () => {
    if (!hasEmpresaSelection) {
      setData([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const result = await api.get(URL_BASE, { params: { idEmbarcador: empresaId } });
      setData(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      setData([]);
      setLoadError("Não foi possível buscar os negócios deste embarcador.");
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao carregar",
        description: "Não foi possível buscar os negócios deste embarcador.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, empresaId, hasEmpresaSelection]);

  useEffect(() => {
    setData([]);
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const query = normalizeText(debouncedSearch);

    return (Array.isArray(data) ? data : []).filter((item) => {
      if (statusFilter !== STATUS_ALL) {
        const ativo = isActive(item.status);
        if (statusFilter === "active" && !ativo) return false;
        if (statusFilter === "inactive" && ativo) return false;
      }

      if (query) {
        const index = normalizeText(
          [item.codigo, item.nome, item.descricao].filter(Boolean).join(" ")
        );
        if (!index.includes(query)) return false;
      }

      return true;
    });
  }, [data, debouncedSearch, statusFilter]);

  const sorted = useMemo(() => sortRows(filtered, sort, sortValue), [filtered, sort]);
  const paged = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize]);

  // Mudou filtro, busca ou ordenação: volta para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, sort, pageSize]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(createEmptyForm());
    setFormErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((item) => {
    setEditing(item);
    setForm({
      codigo: item.codigo || "",
      nome: item.nome || "",
      descricao: item.descricao || "",
      status: isActive(item.status) ? "active" : "inactive",
      padrao: isPadrao(item.padrao),
    });
    setFormErrors({});
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    if (saving) return;
    setFormOpen(false);
  }, [saving]);

  const updateForm = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!form.codigo.trim()) {
      errors.codigo = "Informe o código.";
    } else if (form.codigo.trim().length > 10) {
      errors.codigo = "Máximo de 10 caracteres.";
    }
    if (!form.nome.trim()) {
      errors.nome = "Informe o nome do negócio.";
    } else if (form.nome.trim().length > 40) {
      errors.nome = "Máximo de 40 caracteres.";
    }
    if (form.descricao.trim().length > 255) {
      errors.descricao = "Máximo de 255 caracteres.";
    }
    return errors;
  }, [form]);

  const handleSave = useCallback(async () => {
    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    const payload = {
      idEmbarcador: empresaId,
      codigo: form.codigo.trim(),
      nome: form.nome.trim(),
      descricao: form.descricao.trim(),
      status: form.status === "active",
      padrao: form.padrao,
    };

    try {
      setSaving(true);
      if (editing) {
        await api.put(`${URL_BASE}/${editing.id}`, payload);
      } else {
        await api.post(URL_BASE, payload);
      }

      addToast({
        place: "tr",
        type: "success",
        title: editing ? "Negócio atualizado" : "Negócio cadastrado",
        description: editing
          ? "As alterações foram salvas."
          : "O negócio foi cadastrado com sucesso.",
        icon: "tim-icons icon-check-2",
      });

      setFormOpen(false);
      loadData();
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao salvar",
        description:
          (error && error.response && error.response.data && error.response.data.error) ||
          "Não foi possível salvar o negócio.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [addToast, editing, empresaId, form, loadData, validateForm]);

  const handleDelete = useCallback(async () => {
    if (!toDelete) return;
    try {
      setDeleting(true);
      await api.delete(`${URL_BASE}/${toDelete.id}`);
      addToast({
        place: "tr",
        type: "success",
        title: "Negócio excluído",
        description: `O negócio "${toDelete.nome}" foi removido.`,
        icon: "tim-icons icon-check-2",
      });
      setToDelete(null);
      loadData();
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Não foi possível excluir",
        description:
          (error && error.response && error.response.data && error.response.data.error) ||
          "Erro ao excluir o negócio.",
        icon: "tim-icons icon-alert-circle-exc",
      });
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  }, [addToast, loadData, toDelete]);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setStatusFilter(STATUS_ALL);
  }, []);

  const handleSort = useCallback((field) => setSort((prev) => toggleSort(prev, field)), []);

  const hasFilters = normalizeText(searchTerm) !== "" || statusFilter !== STATUS_ALL;
  const activeFiltersCount = statusFilter !== STATUS_ALL ? 1 : 0;
  const empresaLabel =
    (empresa && (empresa.label || empresa.nome || empresa.Nome)) || "embarcador selecionado";
  const statusOption = STATUS_OPTIONS.find((o) => o.value === statusFilter) || STATUS_OPTIONS[0];

  if (!hasEmpresaSelection) {
    return (
      <div className="content negocios-page">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para gerenciar os negócios." />
      </div>
    );
  }

  const renderListState = () => {
    if (loading) {
      return (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent, marginRight: "0.5rem" }} />
          Carregando negócios…
        </div>
      );
    }
    if (loadError) {
      return (
        <div style={stateBoxStyle}>
          <div style={stateTitleStyle}>Não foi possível carregar</div>
          <p style={{ marginBottom: "0.75rem" }}>{loadError}</p>
          <Button size="sm" color="info" outline onClick={loadData}>
            Tentar novamente
          </Button>
        </div>
      );
    }
    if (sorted.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <div style={stateTitleStyle}>Nenhum negócio encontrado</div>
          <p style={{ marginBottom: 0 }}>
            {hasFilters
              ? "Ajuste os filtros ou limpe a busca."
              : "Cadastre o primeiro negócio usando o botão acima."}
          </p>
        </div>
      );
    }
    return null;
  };

  const listState = renderListState();

  return (
    <div className="content negocios-page">
      {/* Modal de criação/edição: título/close/inputs tematizados por .modal-align-top */}
      <Modal
        isOpen={isFormOpen}
        toggle={closeForm}
        modalClassName="modal-align-top"
        fade={false}
        size="md"
      >
        <ModalHeader toggle={closeForm} tag="h5">
          {editing ? "Editar negócio" : "Novo negócio"}
        </ModalHeader>

        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <div style={modalSubtitleStyle}>{empresaLabel}</div>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="negocio-codigo">Código</Label>
                <Input
                  id="negocio-codigo"
                  value={form.codigo}
                  maxLength={10}
                  onChange={(e) => updateForm("codigo", e.target.value)}
                  invalid={Boolean(formErrors.codigo)}
                  placeholder="Ex.: 01"
                />
                {formErrors.codigo ? <FormFeedback>{formErrors.codigo}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="8">
              <FormGroup>
                <Label for="negocio-nome">Nome</Label>
                <Input
                  id="negocio-nome"
                  value={form.nome}
                  maxLength={40}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  invalid={Boolean(formErrors.nome)}
                  placeholder="Ex.: Venda"
                />
                {formErrors.nome ? <FormFeedback>{formErrors.nome}</FormFeedback> : null}
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="negocio-descricao">Descrição</Label>
            <Input
              id="negocio-descricao"
              type="textarea"
              rows="3"
              value={form.descricao}
              maxLength={255}
              onChange={(e) => updateForm("descricao", e.target.value)}
              invalid={Boolean(formErrors.descricao)}
              placeholder="Descrição opcional do negócio"
            />
            {formErrors.descricao ? (
              <FormFeedback>{formErrors.descricao}</FormFeedback>
            ) : null}
          </FormGroup>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="negocio-status">Status</Label>
                <Input
                  id="negocio-status"
                  type="select"
                  value={form.status}
                  onChange={(e) => updateForm("status", e.target.value)}
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="6" className="d-flex align-items-center">
              <FormGroup check style={{ marginTop: "0.5rem" }}>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={form.padrao}
                    onChange={(e) => updateForm("padrao", e.target.checked)}
                  />
                  <span className="form-check-sign" />
                  Negócio padrão do embarcador
                </Label>
              </FormGroup>
            </Col>
          </Row>

          <div style={modalFooterStyle}>
            <Button color="default" outline size="sm" onClick={closeForm} disabled={saving}>
              Cancelar
            </Button>
            <Button color="info" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Modal de confirmação de exclusão */}
      <Modal
        isOpen={Boolean(toDelete)}
        toggle={() => (deleting ? null : setToDelete(null))}
        modalClassName="modal-align-top"
        fade={false}
        size="sm"
      >
        <ModalHeader toggle={() => (deleting ? null : setToDelete(null))} tag="h5">
          Excluir negócio
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <p style={{ marginBottom: "1.25rem" }}>
            Confirma a exclusão do negócio{" "}
            <strong>{toDelete ? toDelete.nome : ""}</strong>? Esta ação não pode ser desfeita.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button
              color="default"
              outline
              size="sm"
              onClick={() => setToDelete(null)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button color="danger" size="sm" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Excluindo..." : "Excluir"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <PageHero
        eyebrow="Cadastros gerais"
        title="Negócios"
        description={
          <>
            Cadastro de negócios de <strong>{empresaLabel}</strong>, usados na classificação
            de notas, fretes e romaneios.
          </>
        }
        actions={
          <>
            <PageHeroRefreshButton onClick={loadData} loading={loading} />
            <Button color="info" className="btn-sm" onClick={openCreate}>
              <FaPlus style={{ marginRight: 6 }} />
              Novo negócio
            </Button>
          </>
        }
      />

      <FilterBand
        mode="client"
        searchLabel="Buscar negócio"
        searchHint="Código, nome ou descrição"
        searchPlaceholder="Buscar por código, nome ou descrição"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        activeFiltersCount={activeFiltersCount}
        panelTitle="Filtros"
        panelDescription="Refine a lista por status do negócio."
        onClear={hasFilters ? clearFilters : undefined}
        showActions={hasFilters}
      >
        <Row>
          <Col md="4">
            <Label for="negocio-status-filter" style={{ color: AUTOPILOT_COLORS.muted }}>
              Status
            </Label>
            <Select
              inputId="negocio-status-filter"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={STATUS_OPTIONS}
              value={statusOption}
              onChange={(opt) => setStatusFilter(opt ? opt.value : STATUS_ALL)}
              isSearchable={false}
            />
          </Col>
        </Row>
      </FilterBand>

      <ShellCard
        className="negocios-lista"
        title="Negócios cadastrados"
        description={`${sorted.length} resultado(s)`}
      >
          {listState ? (
            listState
          ) : (
            <TableSurface>
              <Table size="sm" responsive hover className="negocios-table hub-thead-accent">
                <thead className="text-primary">
                  <tr>
                    <th style={{ width: "110px" }}>
                      <SortHeader label="Código" field="codigo" sort={sort} onSort={handleSort} />
                    </th>
                    <th>
                      <SortHeader label="Nome" field="nome" sort={sort} onSort={handleSort} />
                    </th>
                    <th>
                      <SortHeader label="Descrição" field="descricao" sort={sort} onSort={handleSort} />
                    </th>
                    <th style={{ width: "100px" }}>
                      <SortHeader label="Padrão" field="padrao" sort={sort} onSort={handleSort} />
                    </th>
                    <th style={{ width: "110px" }}>
                      <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                    </th>
                    <th className="text-right" style={{ width: "110px" }}>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paged.items.map((item) => {
                    const ativo = isActive(item.status);
                    const padrao = isPadrao(item.padrao);
                    return (
                      <tr key={item.id}>
                        <td>{item.codigo || "—"}</td>
                        <td>{item.nome}</td>
                        <td style={item.descricao ? undefined : mutedCellStyle}>
                          {item.descricao || "—"}
                        </td>
                        <td>
                          {padrao ? (
                            <Badge color="info" pill title="Negócio padrão">
                              <FaStar style={{ marginRight: 4 }} />
                              Padrão
                            </Badge>
                          ) : (
                            <span style={mutedCellStyle}>—</span>
                          )}
                        </td>
                        <td>
                          <Status status={ativo ? "Ativo" : "Inativo"} title={ativo ? "Ativo" : "Inativo"} />
                        </td>
                        <td className="text-right">
                          <Button
                            color="link"
                            size="sm"
                            className="btn-icon"
                            title="Editar"
                            onClick={() => openEdit(item)}
                          >
                            <FaPen />
                          </Button>
                          <Button
                            color="link"
                            size="sm"
                            className="btn-icon text-danger"
                            title="Excluir"
                            onClick={() => setToDelete(item)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <TablePagination
                page={paged.page}
                pageSize={pageSize}
                total={paged.total}
                itemLabel="negócio(s)"
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </TableSurface>
          )}
      </ShellCard>
    </div>
  );
};

export default Negocios;
